import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { useSiteContext } from '../../context/SiteContext';
import * as roomServices from '../../services/roomServices';
import logger from '../../utils/logger';

export function LeaveScreenNew({ setIsMeetingLeft, meetingId }) {
  const [selectedRating, setSelectedRating] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [audioQuality, setAudioQuality] = useState("Excellent");
  const [videoQuality, setVideoQuality] = useState("Excellent");
  const [lastMeetingSession, setLastMeetingSession] = useState(null);
  const { meetingSettings } = useSiteContext();

  const navigate = useNavigate();

  useEffect(() => {
    getLastMeetingSession();
  }, []);


  const getLastMeetingSession = async () => {
    const response = await roomServices.getLastMeetingSession(meetingSettings.roomId);
    setLastMeetingSession(response.data);
  };

  const handleRatingSelect = (rating) => {
    setSelectedRating(rating);
  };

  const handleSubmitFeedback = async () => {

    if (!selectedRating) {
      toast.error("Please select a rating before submitting feedback.", {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: true,
        closeButton: false,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
      return;
    }

    // Handle feedback submission here
    logger("log", "Rating:", selectedRating);
    logger("log", "Feedback:", feedback);
    logger("log", "Audio Quality:", audioQuality);
    logger("log", "Video Quality:", videoQuality);


    const foundEmoji = emojis.find((emoji) => emoji.id === selectedRating);

    const feedbackData = {
      rating: foundEmoji?.label || null,
      feedback: feedback,
      audioQuality: audioQuality,
      videoQuality: videoQuality,
      roomId: meetingSettings.roomId,
      name: localStorage.getItem("participantName"),
    };

    try {
      const response = await roomServices.submitFeedback(feedbackData);
      logger("log", response);
      navigate("/");
    } catch (error) {
      logger("log", error);
    }
  };

  const handleRejoinRoom = () => {
    const rejoinId = localStorage.getItem("rejoinId");
    localStorage.removeItem("rejoinId");
    window.location.href = rejoinId;
    // setIsMeetingLeft(false);
  };

  const emojis = [
    { id: 1, emoji: "/icons/veryHappy.png", label: "Very happy" },
    { id: 2, emoji: "/icons/happy.png", label: "Happy" },
    { id: 3, emoji: "/icons/neutral.png", label: "Neutral" },
    { id: 4, emoji: "/icons/sad.png", label: "Sad" },
    { id: 5, emoji: "/icons/verySad.png", label: "Very sad" },
  ];

  const qualityOptions = ["Excellent", "Good", "Fair", "Poor"];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#000000] to-[#3A285E] flex items-center justify-center py-8 md:py-16 px-4">
      <div className="max-w-4xl w-full mx-2 md:mx-4">
        {/* Header Section */}
        <div className="text-center mb-4 md:mb-6">
          {/* Logo */}
          <div className="mb-3 md:mb-4">
            <img
              alt="R-Link"
              className="h-12 md:h-16 w-auto mx-auto"
              src="r-link-fav.svg"
            />
          </div>

          {/* Session Complete */}
          <h1 className="text-white text-2xl md:text-4xl font-bold mb-2 uppercase tracking-widest">
            Session Complete
          </h1>

          {/* Subtitle */}
          <p className="text-white text-base md:text-lg mb-6 md:mb-10 tracking-widest px-4">
            Your meeting has ended successfully.
          </p>

          {/* Thank you message */}
          <div className="border-[0.5px] border-white rounded-lg p-3 md:p-4 mb-4 md:mb-6 mx-2">
            <p className="text-[#00C853] text-sm md:text-lg">
              Thanks for using R-Link, your space for meaningful, rewarding
              conversations.
            </p>
          </div>

          {/* Meeting Details */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-3 md:gap-6 mb-6 md:mb-8 text-white px-4">
            <div className="flex items-center gap-2">
              <img
                src="/icons/duration.png"
                alt="time"
                className="w-4 md:w-5"
              />
              <span className="text-sm md:text-base">Duration: {lastMeetingSession?.duration}</span>
            </div>
            <div className="flex items-center gap-2">
              <img
                src="/icons/participants.png"
                alt="participants"
                className="w-4 md:w-5"
              />
              <span className="text-sm md:text-base">{lastMeetingSession?.totalParticipants || 0} Participants</span>
            </div>
            <div className="flex items-center gap-2">
              <img
                src="/icons/connection.png"
                alt="connection"
                className="w-4 md:w-5"
              />
              <span className="text-sm md:text-base">
                Connection: Excellent
              </span>
            </div>
          </div>

          {/* Dashboard Button */}
          <button
            onClick={() => {
              window.open("https://r-link.com", "_blank");
            }}
            className="bg-[#7700FF] hover:bg-[#7700FF]/80 text-white px-6 md:px-8 text-xs md:text-sm tracking-wide py-2 rounded-2xl font-medium transition-colors w-full max-w-xs md:w-auto"
          >
            Start Earning With R-Link
          </button>
        </div>

        {/* Feedback Section */}
        <div className="mb-6 md:mb-8 bg-black/20 rounded-2xl md:rounded-3xl p-4 md:p-6 w-full md:w-4/5 mx-auto">
          <h2 className="text-white text-lg md:text-xl font-semibold mb-3 md:mb-4 text-center uppercase tracking-wide">
            How was your call
          </h2>

          {/* Emoji Ratings */}
          <div className="flex justify-center gap-2 md:gap-4 mb-4 md:mb-4">
            {emojis.map((emoji) => (
              <button
                key={emoji.id}
                onClick={() => handleRatingSelect(emoji.id)}
                className={`p-2 md:p-3 rounded-full transition-all duration-300 ${selectedRating === emoji.id
                  ? "bg-gradient-to-r from-purple-500 to-pink-500 scale-110 shadow-lg shadow-purple-500/50 ring-2 ring-purple-300 ring-offset-2 ring-offset-transparent"
                  : "hover:bg-gray-700 hover:scale-105"
                  }`}
                title={emoji.label}
              >
                <img
                  src={emoji.emoji}
                  alt={emoji.label}
                  className={`w-8 h-8 md:w-12 md:h-12 object-contain transition-all duration-300 ${selectedRating === emoji.id
                    ? "brightness-110 contrast-110"
                    : "hover:brightness-110"
                    }`}
                />
              </button>
            ))}
          </div>

          {/* Quality Dropdowns */}
          <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8 mb-3">
            <div className="w-full md:w-40">
              <label className="block text-white text-sm font-medium mb-2 uppercase tracking-wide text-center">
                Audio Quality
              </label>
              <select
                value={audioQuality}
                onChange={(e) => setAudioQuality(e.target.value)}
                className="w-full bg-[#000000]/20 border border-white rounded-lg px-2 py-2 text-white focus:outline-none focus:ring-1 focus:ring-purple-500 text-sm md:text-base"
              >
                {qualityOptions.map((option) => (
                  <option
                    key={option}
                    value={option}
                    className="bg-gray-800 text-white"
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full md:w-40">
              <label className="block text-white text-sm font-medium mb-2 uppercase tracking-wide text-center">
                Video Quality
              </label>
              <select
                value={videoQuality}
                onChange={(e) => setVideoQuality(e.target.value)}
                className="w-full bg-[#000000]/20 border border-white rounded-lg px-2 py-2 text-white focus:outline-none focus:ring-1 focus:ring-purple-500 text-sm md:text-base"
              >
                {qualityOptions.map((option) => (
                  <option
                    key={option}
                    value={option}
                    className="bg-gray-800 text-white"
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Text Area */}
          <div className="mb-4">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Share your thoughts with us (optional)"
              className="w-full h-24 md:h-32 bg-[#D9D9D9] rounded-lg p-3 md:p-4 text-gray-900 placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 border border-white text-sm md:text-base"
            />
          </div>

          {/* Submit Feedback Button */}
          <div className="flex w-full items-center justify-center">
            <button
              onClick={handleSubmitFeedback}
              className="bg-[#7700FF] hover:bg-[#7700FF]/80 text-white px-6 md:px-8 text-xs md:text-sm tracking-wide py-2 rounded-2xl font-medium transition-colors w-full max-w-xs md:w-auto"
            >
              Submit Feedback
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
